/**
 * Notifications, as the browser sees them (§102).
 *
 * ── Where the icon is decided ───────────────────────────────────────────────
 *
 * The API sends a `kind`, a `topic` and an `href`; this file turns them into a
 * shape and a name. That split is the point: the backend owns what HAPPENED, the
 * view owns what it looks like, and neither has to be redeployed when the other
 * changes its mind.
 *
 * The lookup GUESSES, in three tiers, and the later tiers are what stop a
 * notification ever rendering as a blank square:
 *
 *   1. the exact `kind` — a refund is a different drawing from a purchase;
 *   2. failing that, where the row POINTS — a notice linking to a member's page
 *      is about a member, whatever its kind was called;
 *   3. failing that, the `topic` — an unknown `credit.*` still gets the coin.
 *
 * So a kind added to the backend catalogue tomorrow ships something sensible
 * today, and specificity is an improvement rather than a prerequisite. Adding a
 * kind to `BY_KIND` is how a guess becomes an answer.
 *
 * NO `server-only` import here, deliberately: the bell is a client component and
 * the notifications page is a server one, and both need these paths.
 */

export type NotificationTone = 'info' | 'success' | 'warn' | 'danger'

/** One labelled figure — "New balance", "1,240". See the backend's domain type. */
export type NotificationDetail = {
  label: string
  value: string
}

export type NotificationRecord = {
  id: string
  audience: 'admin' | 'user'
  kind: string
  topic: string
  tone: NotificationTone
  title: string
  body: string
  /**
   * Optional on the way IN, always an array once read through `detailsOf`.
   * A row written before the field existed has none, and a page that maps over
   * `undefined` is a blank screen rather than a missing chip.
   */
  details?: NotificationDetail[]
  href: string | null
  actor: string | null
  readAt: string | null
  createdAt: string
}

/** The details, safely — see the note on the field. */
export function detailsOf(item: Pick<NotificationRecord, 'details'>): NotificationDetail[] {
  return Array.isArray(item.details) ? item.details : []
}

export type NotificationPage = {
  notifications: NotificationRecord[]
  /** Unread across the account, whatever the filter — the badge's number. */
  unread: number
  /** Everything on the account, whatever the filter — the header's number. */
  total: number
  /**
   * How many match the filter that was sent. The pager's number, and only ever
   * that: paging on `total` while filtered offers pages that come back empty.
   * Optional because the bell asks for a page without one and does not read it.
   */
  matching?: number
}

/**
 * 20×20 stroke paths, drawn to the same weight as the console's other icons so a
 * notification list sits beside a KPI row without looking imported.
 */
const GLYPH = {
  /** A person with a plus — a new account, not "people". */
  userAdd: 'M8 3.4a3.2 3.2 0 110 6.4 3.2 3.2 0 010-6.4zM2.6 16.6c0-3 2.4-5 5.4-5 1 0 2 .2 2.8.7M14 12v5M11.5 14.5h5',
  /** A shield: what this account is permitted to do. */
  shield: 'M10 2.5l6 2.5v5c0 3.6-2.5 6.6-6 7.5-3.5-.9-6-3.9-6-7.5V5z',
  /** A shield with a slash — permission taken away, or an account stopped. */
  shieldOff: 'M10 2.5l6 2.5v5c0 3.6-2.5 6.6-6 7.5-3.5-.9-6-3.9-6-7.5V5zM5 5l10 10',
  /** A key. Credentials changed. */
  key: 'M12.6 3.6a3.9 3.9 0 11-3.3 5.9L3.5 15.3v2.2h2.2v-1.8h1.8v-1.8h1.8l1.3-1.3a3.9 3.9 0 012-8.9z',
  /** A coin with a value stroke — the same one the dashboard's Credits nav uses. */
  coin: 'M10 3.5a6.5 6.5 0 110 13 6.5 6.5 0 010-13zM10 6.5v7M8 8.2h3.2M8 11.8h3.2',
  /** A magnifier: discovery went and looked. */
  search: 'M9 3.5a5.5 5.5 0 105.5 5.5A5.5 5.5 0 009 3.5zM13.1 13.1l3.4 3.4',
  /** A magnifier that came back empty-handed. */
  searchFail: 'M9 3.5a5.5 5.5 0 105.5 5.5A5.5 5.5 0 009 3.5zM13.1 13.1l3.4 3.4M7.2 7.2l3.6 3.6M10.8 7.2l-3.6 3.6',
  /** The robot, as the console draws it everywhere else. */
  robot: 'M5.5 7.5h9v7h-9zM10 4.2v3.3M3.5 9.5v3M16.5 9.5v3M8.2 10.5h.01M11.8 10.5h.01',
  /** A speech bubble. Somebody wrote something. */
  comment: 'M3.2 5.2h13.6v8.2H9.4L6 16.5v-3.1H3.2z',
  /** The stacked corpus mark — an entity entered the index. */
  corpus: 'M10 2.8l7 3.4-7 3.4-7-3.4zM3 10.2l7 3.4 7-3.4M3 13.9l7 3.3 7-3.3',
  /** A megaphone: something is being promoted. */
  megaphone: 'M4.2 8.2v3.6h2.4l6.6 3.4V4.8L6.6 8.2zM4.2 8.2H3v3.6h1.2M15.6 8a3 3 0 010 4M6.6 11.8v3.4h2v-2.4',
  /** A price tag with its hole — a listing somebody has taken ownership of. */
  tag: 'M10.4 3.2H16v5.6l-7.4 7.4-5.6-5.6zM13.2 6.1h.01',
  /** A bell, for an announcement with no better home. */
  bell: 'M10 3.2a4.4 4.4 0 014.4 4.4c0 3.5 1.1 4.6 1.1 4.6H4.5s1.1-1.1 1.1-4.6A4.4 4.4 0 0110 3.2zM8.4 15a1.7 1.7 0 003.2 0',
} as const

/**
 * What a row is about: a drawing and the name of the thing.
 *
 * The label is not decoration. An icon alone is a rebus — the reader learns what
 * a coin means by reading the notification beside it, which is the wrong way
 * round — and it is also the only thing a screen reader can say about a shape.
 * So every icon in this file carries the word it stands for, and the word is
 * what goes in `title` and `aria-label` at the call site.
 */
export type NotificationIcon = {
  path: string
  /** Two words at most, capitalised as a noun. "Credits", "Robot run". */
  label: string
}

const icon = (path: string, label: string): NotificationIcon => ({ path, label })

/** Exact kind → icon. The specific tier. */
const BY_KIND: Record<string, NotificationIcon> = {
  'account.registered': icon(GLYPH.userAdd, 'New member'),
  'account.role_changed': icon(GLYPH.shield, 'Permissions'),
  'account.status_changed': icon(GLYPH.shieldOff, 'Account status'),
  'account.password_changed': icon(GLYPH.key, 'Password'),
  'credit.purchased': icon(GLYPH.coin, 'Purchase'),
  'credit.granted': icon(GLYPH.coin, 'Credit grant'),
  'credit.revoked': icon(GLYPH.coin, 'Credits removed'),
  'credit.refunded': icon(GLYPH.coin, 'Refund'),
  'discovery.completed': icon(GLYPH.search, 'Discovery'),
  'discovery.ready': icon(GLYPH.search, 'Discovery'),
  'discovery.failed': icon(GLYPH.searchFail, 'Discovery'),
  'robot.run_failed': icon(GLYPH.robot, 'Robot run'),
  'comment.posted': icon(GLYPH.comment, 'Comment'),
  'entity.created': icon(GLYPH.corpus, 'Entity'),
  'ads.listing_claimed': icon(GLYPH.tag, 'Listing'),
  'ads.campaign_started': icon(GLYPH.megaphone, 'Campaign'),
  'ads.campaign_ended': icon(GLYPH.megaphone, 'Campaign'),
}

/**
 * Where it POINTS → icon. The middle tier, and the reason it exists.
 *
 * `system.notice` is the catch-all kind, so it is the one kind that tells the
 * view nothing — and it is also the kind every future announcement will arrive
 * as before anybody gets round to adding a proper one. But an announcement
 * always links somewhere, and where it links is what it is about: a notice
 * pointing at `/dashboard/credits` is a credits notice whatever its kind says.
 *
 * Checked in order, longest prefix first, because `/admin/robot/discovery/…` is
 * a discovery and `/admin/robot/runs/…` is a run and both start `/admin/robot`.
 */
const BY_HREF: ReadonlyArray<readonly [string, NotificationIcon]> = [
  ['/admin/robot/discovery', icon(GLYPH.search, 'Discovery')],
  ['/admin/robot/runs', icon(GLYPH.robot, 'Robot run')],
  ['/admin/robot', icon(GLYPH.robot, 'Robot')],
  ['/admin/credits', icon(GLYPH.coin, 'Credits')],
  ['/admin/users', icon(GLYPH.userAdd, 'Member')],
  ['/admin/ads', icon(GLYPH.megaphone, 'Ads')],
  ['/dashboard/credits', icon(GLYPH.coin, 'Credits')],
  ['/dashboard/security', icon(GLYPH.key, 'Security')],
  ['/dashboard/notifications', icon(GLYPH.bell, 'Notifications')],
  ['/dashboard/listings', icon(GLYPH.tag, 'Listing')],
  ['/dashboard/ads', icon(GLYPH.megaphone, 'Campaign')],
  ['/entity/', icon(GLYPH.corpus, 'Entity')],
  ['/search', icon(GLYPH.search, 'Search')],
  ['/admin', icon(GLYPH.shield, 'Console')],
]

/** Topic → icon. The last tier, so an unknown kind still draws something. */
const BY_TOPIC: Record<string, NotificationIcon> = {
  account: icon(GLYPH.shield, 'Account'),
  credit: icon(GLYPH.coin, 'Credits'),
  discovery: icon(GLYPH.search, 'Discovery'),
  entity: icon(GLYPH.corpus, 'Entity'),
  robot: icon(GLYPH.robot, 'Robot'),
  comment: icon(GLYPH.comment, 'Comment'),
  ads: icon(GLYPH.megaphone, 'Advertising'),
  system: icon(GLYPH.bell, 'Announcement'),
}

const FALLBACK = icon(GLYPH.bell, 'Notification')

/**
 * Guess what a row is about, from what the row already carries.
 *
 * Three tiers, most specific first — kind, then destination, then topic — and
 * the guess never fails: the bell is what an unrecognised anything looks like.
 *
 * Nothing is added to the API to make this work, which is the point. The server
 * says what happened and where it leads; deciding that "leads to /entity/…"
 * means "draw the corpus mark" is a view decision, and keeping it here means a
 * new kind ships with a sensible icon on the day it is emitted rather than on
 * the day both sides are redeployed together.
 */
export function iconFor(
  item: Pick<NotificationRecord, 'kind' | 'topic' | 'href'>,
): NotificationIcon {
  const exact = BY_KIND[item.kind]
  if (exact) return exact

  if (item.href) {
    const hit = BY_HREF.find(([prefix]) => item.href!.startsWith(prefix))
    if (hit) return hit[1]
  }

  return BY_TOPIC[item.topic] ?? FALLBACK
}

/** The path alone, for callers that already know what they are drawing. */
export function glyphFor(kind: string, topic: string, href?: string | null): string {
  return iconFor({ kind, topic, href: href ?? null }).path
}

/**
 * The badge's number, as a badge can render it.
 *
 * Anything past 99 is "lots" — three digits do not fit in a dot on an avatar,
 * and the difference between 100 and 340 unread changes nobody's next action.
 */
export function badgeCount(n: number): string {
  return n > 99 ? '99+' : String(n)
}
